// components/portal/PortalUrlSynchronizer.tsx
'use client';

import { memo, useCallback, useEffect, useRef, useState, Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { parsePortalUrlState, parseSlidesDeepLink } from '#lib/leasing/portal-url-state';
import { useSlidesStore } from '@/components/slides/slidesStore';
import { openMerchPlan, patchPulse } from '@/components/footfall/pulseState';

const UrlSynchronizerComponent = memo(({ onSessionRestoringChange }: { onSessionRestoringChange?: (restoring: boolean) => void }) => {
  const { kernel } = useKernel();
  const pathname = usePathname();
  
  const activeChatSessionId = useKernelStore(state => state.activeChatSessionId);
  const inventoryInitStatus = useKernelStore(state => state.inventoryInitStatus);

  const hasAppliedInitialUrl = useRef(false);
  const hasHandledWorkerTab = useRef(false);
  const isApplyingPopState = useRef(false);
  const [urlRequestedSessionId, setUrlRequestedSessionId] = useState<string | null>(() => {
    const entry = parsePortalUrlState(window.location.search);
    return entry.focusTarget ? null : entry.chatSessionId;
  });
  useEffect(() => {
    onSessionRestoringChange?.(Boolean(urlRequestedSessionId));
  }, [onSessionRestoringChange, urlRequestedSessionId]);

  const activateRequestedSession = useCallback(async (sessionId: string, isStillWanted: () => boolean) => {
    const k = kernel.current;
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!k || !normalizedSessionId) return;
    try {
      await k.activateChatSession(normalizedSessionId, { isStillWanted });
      if (!isStillWanted()) return;
      const activatedSessionId = k.getActiveChatSessionId();
      if (activatedSessionId !== normalizedSessionId) {
        console.warn('[UrlSync] URL-requested chat session is unavailable after activation attempt.', {
          sessionId: normalizedSessionId,
        });
        k.clearActiveChatSession();
      }
    } catch (error) {
      if (!isStillWanted()) return;
      console.warn('[UrlSync] Failed to activate URL-requested chat session.', {
        sessionId: normalizedSessionId,
        error,
      });
      k.clearActiveChatSession();
    } finally {
      if (isStillWanted()) setUrlRequestedSessionId(null);
    }
  }, [kernel]);

  // Helper to apply URL to state
  const applyUrlToState = useCallback((searchString: string) => {
    const k = kernel.current;
    if (!k) return;
    const parsed = parsePortalUrlState(searchString);
    if (parsed.focusTarget) {
      setUrlRequestedSessionId(null);
      k.applyPortalFocusTarget(parsed.focusTarget);
      return;
    }
    if (parsed.chatSessionId) {
      setUrlRequestedSessionId(parsed.chatSessionId);
    } else {
      setUrlRequestedSessionId(null);
      k.clearActiveChatSession();
    }
  }, [kernel]);

  // 1. Initial Load (Once)
  useEffect(() => {
    if (hasAppliedInitialUrl.current) return;
    
    console.log('[UrlSync] Applying initial URL to state.');
    // A Slides deep link (?sd=<deck>&sp=<page>&se=<element>) is recorded here, before the
    // state-to-URL effect below rewrites the address and loses it. Portal takes the user into
    // Slides once permissions are known; SlidesView applies the page and element once the deck
    // has loaded.
    const slidesLink = parseSlidesDeepLink(window.location.search);
    if (slidesLink) {
      const slides = useSlidesStore.getState();
      slides.setPendingFocus(slidesLink);
      slides.setDashboardView('decks');
    }
    applyUrlToState(window.location.search);
    hasAppliedInitialUrl.current = true;
  }, [applyUrlToState]);

  // Worker-tab entry (master orchestration): the Master opens a project in a
  // STANDALONE tab with ?p=<projectId>&q=<brief>&qi=<ref|urls>. This tab reads
  // those params, opens the project, and quickstarts the brief — then strips the
  // params so a reload doesn't re-run it. This is how the operator (master) view
  // stays put while the actual design work happens in its own tab. Runs once,
  // after inventory is ready.
  useEffect(() => {
    if (hasHandledWorkerTab.current) return;
    if (inventoryInitStatus !== 'initialized') return;
    const k = kernel.current;
    if (!k) return;
    const params = new URLSearchParams(window.location.search);
    const masterQuickstart = params.get('mq')?.trim() || '';
    const masterProjectId = params.get('mp')?.trim() || '';
    const legacyProjectId = params.get('p')?.trim() || '';
    if (!masterQuickstart && !legacyProjectId) return;
    hasHandledWorkerTab.current = true;
    const imageUrls = (params.get('qi') || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);
    const images = imageUrls.length ? imageUrls : undefined;
    void (async () => {
      try {
        if (masterQuickstart) {
          // Primary path: ?c has already activated the studio+Project session (the
          // project editor is opening). Wait for it to settle, then run the first
          // Designer turn so the worker tab actually GENERATES the brief.
          for (let i = 0; i < 30 && !k.getActiveChatSessionId(); i += 1) {
            await new Promise((r) => setTimeout(r, 150));
          }
          const sid = k.getActiveChatSessionId();
          if (sid) {
            await k.execute(masterQuickstart, images, {
              agentMode: 'director',
              projectId: masterProjectId,
            });
          }
        } else if (legacyProjectId) {
          // Fallback (no pre-created session): open the project directly.
          await k.syncEntity('Project', legacyProjectId).catch(() => undefined);
          const proj = k.getInventory().Project.registry[legacyProjectId];
          const userId = (typeof proj?.userId === 'string' && proj.userId) || k.getUserContext().dbUserId || '';
          k.openProject(legacyProjectId, userId, params.get('q')?.trim() || undefined, images);
        }
      } catch (error) {
        console.error('[workerTab] failed to run master quickstart', error);
      } finally {
        // Strip the master/quickstart params (KEEP ?c) so a reload doesn't re-run.
        const keep = new URLSearchParams(window.location.search);
        ['mq', 'mp', 'qi', 'p', 'q'].forEach((key) => keep.delete(key));
        const qs = keep.toString();
        window.history.replaceState(null, '', qs ? `${pathname}?${qs}` : pathname);
      }
    })();
  }, [inventoryInitStatus, pathname]);

  // A merchandising plan opened from Source in its own window (?plan=<propertyId>[&campaign=<campaignId>],
  // components/footfall/pulseState.ts openMerchPlan). Read at the first render: the state -> URL sync below
  // rewrites the address before the inventory is ready.
  const merchPlanEntry = useRef(typeof window === 'undefined' ? null : new URLSearchParams(window.location.search));
  useEffect(() => {
    const params = merchPlanEntry.current;
    const k = kernel.current;
    const propertyId = params?.get('plan')?.trim();
    if (!params || !k || !propertyId || inventoryInitStatus !== 'initialized') return;
    merchPlanEntry.current = null;
    void openMerchPlan(k, propertyId, params.get('campaign')?.trim() || null)
      .then((error) => { if (error) patchPulse(k, { error }); })
      .catch(() => k.handleError('The merch plan could not be opened.'));
  }, [inventoryInitStatus]);

  // 2. Handle Back/Forward browser buttons
  useEffect(() => {
    const handlePopState = () => {
      isApplyingPopState.current = true;
      applyUrlToState(window.location.search);
      // Reset flag after a short delay to allow state to settle
      setTimeout(() => { isApplyingPopState.current = false; }, 100);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [applyUrlToState]);

  // Activate URL-requested session only after inventory bootstrap is complete.
  // This avoids the race where initializeInventory clears TimelineEvent right after activation.
  useEffect(() => {
    if (inventoryInitStatus !== 'initialized') return;
    if (!urlRequestedSessionId) return;
    let current = true;
    void activateRequestedSession(urlRequestedSessionId, () => current);
    return () => { current = false; };
  }, [activateRequestedSession, inventoryInitStatus, urlRequestedSessionId]);

  // 3. State -> URL (Native replaceState, no re-renders)
  useEffect(() => {
    if (!hasAppliedInitialUrl.current || urlRequestedSessionId || isApplyingPopState.current || !kernel.current) return;

    const params = new URLSearchParams();

    if (activeChatSessionId) {
      params.set('c', activeChatSessionId);
    }

    const newParamsString = params.toString();
    const currentSearch = window.location.search.replace('?', '');
    
    if (newParamsString !== currentSearch) {
      const newUrl = newParamsString ? `${pathname}?${newParamsString}` : pathname;
      window.history.replaceState(null, '', newUrl);
    }
  }, [activeChatSessionId, pathname, kernel, urlRequestedSessionId]);

  return null;
});
UrlSynchronizerComponent.displayName = 'UrlSynchronizerComponent';

export const UrlSynchronizer = ({ onSessionRestoringChange }: { onSessionRestoringChange?: (restoring: boolean) => void }) => (
  <Suspense fallback={null}>
    <UrlSynchronizerComponent onSessionRestoringChange={onSessionRestoringChange} />
  </Suspense>
);
